import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Card,
  CardContent,
  Divider,
  Grid,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Edit, Delete, ToggleOn, ToggleOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { subUserAPI, pricingAPI } from '../../services/api';

const SubUsers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [subUsers, setSubUsers] = useState([]);
  const [verificationTypes, setVerificationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    allowedServices: []
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedSubUser, setSelectedSubUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subUserToDelete, setSubUserToDelete] = useState(null);

  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subUsersRes, pricingRes] = await Promise.all([
        subUserAPI.getAll(),
        pricingAPI.getAll()
      ]);
      setSubUsers(subUsersRes.data || []);
      setVerificationTypes(pricingRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load sub-users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (subUser = null) => {
    if (subUser) {
      setEditMode(true);
      setSelectedSubUser(subUser);
      
      // Fetch full details including permissions
      try {
        const details = await subUserAPI.getDetails(subUser.id || subUser.subUserId);
        setFormData({
          firstName: details.data.firstName || subUser.firstName,
          lastName: details.data.lastName || subUser.lastName,
          email: details.data.email || subUser.email,
          phoneNumber: details.data.phone || subUser.phone || '',
          password: '',
          allowedServices: details.data.selectedServices || []
        });
      } catch (error) {
        // Fallback if details fetch fails
        setFormData({
          firstName: subUser.firstName,
          lastName: subUser.lastName,
          email: subUser.email,
          phoneNumber: subUser.phone || '',
          password: '',
          allowedServices: []
        });
      }
    } else {
      setEditMode(false);
      setSelectedSubUser(null);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        allowedServices: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      allowedServices: []
    });
  };

  const handleSendOTP = async () => {
    if (!formData.email || !formData.firstName || !formData.lastName) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!editMode) {
      if (!formData.password) {
        toast.error('Please enter a password');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
    }

    try {
      await subUserAPI.sendOTP(formData.email, `${formData.firstName} ${formData.lastName}`);
      toast.success('OTP sent to email');
      setDialogOpen(false);
      setOtpDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    }
  };

  const handleDirectUpdate = async () => {
    try {
      await subUserAPI.update(selectedSubUser.id || selectedSubUser.subUserId, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        phoneNumber: formData.phoneNumber,
        selectedServices: formData.allowedServices
      });
      toast.success('Sub-user updated successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Update failed');
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyAndCreate = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    try {
      await subUserAPI.verifyOTP(formData.email, otpString);
      
      if (editMode) {
        await subUserAPI.update(selectedSubUser.id || selectedSubUser.subUserId, {
          ...formData,
          selectedServices: formData.allowedServices
        });
        toast.success('Sub-user updated successfully');
      } else {
        await subUserAPI.create({
          ...formData,
          selectedServices: formData.allowedServices,
          otp: otpString
        });
        toast.success('Sub-user created successfully');
      }
      
      setOtpDialogOpen(false);
      setOtp(['', '', '', '']);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleToggleStatus = async (subUserId) => {
    try {
      await subUserAPI.toggleStatus(subUserId);
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getServiceNames = (serviceIds) => {
    if (!serviceIds || serviceIds.length === 0) return 'None';
    return verificationTypes
      .filter((type) => serviceIds.includes(type.id))
      .map((type) => type.name)
      .join(', ');
  };

  const handleDelete = (subUserId) => {
    setSubUserToDelete(subUserId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await subUserAPI.delete(subUserToDelete);
      toast.success('Sub-user deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete sub-user');
    } finally {
      setDeleteDialogOpen(false);
      setSubUserToDelete(null);
    }
  };

  // Mobile Card Component
  const MobileCard = ({ subUser, index }) => (
    <Card
      sx={{
        mb: 1.5,
        mx: { xs: 0, md: 0 },
        borderRadius: { xs: 0, md: 2 },
        boxShadow: 2,
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack spacing={1.25}>
          {/* Header Row */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              #{index + 1}
            </Typography>
            <Chip
              label={subUser.isActive ? 'Active' : 'Inactive'}
              color={subUser.isActive ? 'success' : 'default'}
              size="small"
            />
          </Box>

          {/* Name */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Name
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {subUser.firstName} {subUser.lastName}
            </Typography>
          </Box>

          <Divider />

          {/* Contact Info */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Email
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, wordBreak: 'break-word' }}
              >
                {subUser.email}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Phone Number
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {subUser.phoneNumber || 'Not provided'}
              </Typography>
            </Grid>
          </Grid>

          <Divider />

          {/* Allowed Services */}
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Allowed Services
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {subUser.allowedServices?.length || 0} services
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleOpenDialog(subUser)}
              sx={{ bgcolor: 'primary.50' }}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(subUser.id)}
              sx={{ bgcolor: 'error.50' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 0, sm: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: { xs: 2, md: 4 }, gap: 2, px: { xs: 2, md: 0 } }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', md: '2.125rem' } }}>
              Sub Users
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Manage sub-users and their permissions
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Add Sub User
          </Button>
        </Box>

        {/* Mobile View */}
        {isMobile ? (
          <Box sx={{ px: { xs: 2, md: 0 } }}>
            {subUsers.length === 0 ? (
              <Paper sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No sub-users found. Click "Add Sub User" to create one.
                </Typography>
              </Paper>
            ) : (
              subUsers.map((subUser, index) => (
                <MobileCard key={subUser.id} subUser={subUser} index={index} />
              ))
            )}
          </Box>
        ) : (
          /* Desktop Table View */
          <TableContainer component={Paper} sx={{ overflowX: 'auto', borderRadius: 2, boxShadow: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Allowed Services</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                      <Typography variant="body1" color="text.secondary">
                        No sub-users found. Click "Add Sub User" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  subUsers.map((subUser, index) => (
                    <TableRow key={subUser.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{subUser.firstName} {subUser.lastName}</TableCell>
                      <TableCell>{subUser.email}</TableCell>
                      <TableCell>{subUser.phoneNumber || '-'}</TableCell>
                      <TableCell>
                        {subUser.allowedServices?.length || 0} services
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subUser.isActive ? 'Active' : 'Inactive'}
                          color={subUser.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(subUser)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(subUser.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{editMode ? 'Edit Sub User' : 'Add Sub User'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                required
              />
              <TextField
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                required
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                disabled={editMode}
              />
              <TextField
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                autoComplete="new-phone"
                inputProps={{ autoComplete: 'new-phone' }}
              />
              {!editMode && (
                <TextField
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  autoComplete="new-password"
                  inputProps={{ autoComplete: 'new-password' }}
                />
              )}
              {!editMode && (
                <TextField
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                  autoComplete="new-password"
                  inputProps={{ autoComplete: 'new-password' }}
                  error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                  helperText={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword ? 'Passwords do not match' : ''}
                />
              )}
              <FormControl>
                <InputLabel>Allowed Services</InputLabel>
                <Select
                  multiple
                  value={formData.allowedServices}
                  onChange={(e) => setFormData({ ...formData, allowedServices: e.target.value })}
                  input={<OutlinedInput label="Allowed Services" />}
                  renderValue={(selected) => `${selected.length} selected`}
                >
                  {verificationTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      <Checkbox checked={formData.allowedServices.includes(type.id)} />
                      <ListItemText primary={type.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {editMode ? (
              <Button onClick={handleDirectUpdate} variant="contained">
                Update
              </Button>
            ) : (
              <Button onClick={handleSendOTP} variant="contained">
                Send OTP
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'error.50',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Delete sx={{ color: 'error.main', fontSize: 20 }} />
              </Box>
              <Typography variant="h6" fontWeight={600}>
                Delete Sub User
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to delete this sub-user? This action cannot be undone and will permanently remove their account and all associated permissions.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              fullWidth
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              fullWidth
              sx={{ borderRadius: 2 }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* OTP Dialog */}
        <Dialog open={otpDialogOpen} onClose={() => setOtpDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Verify OTP</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter the OTP sent to {formData.email}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={otpRefs[index]}
                  value={digit}
                  onChange={(e) => handleOTPChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  inputProps={{
                    maxLength: 1,
                    style: { textAlign: 'center', fontSize: '24px' }
                  }}
                  sx={{ width: '60px' }}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOtpDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleVerifyAndCreate} variant="contained">
              Verify & {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default SubUsers;
